import { useCallback, useEffect, useRef, useState } from 'react';
import { syncSupportChat } from '../services/supportChatService';

/**
 * Polling adaptativo para chat de suporte.
 * Ativo ~1.5s; idle ~5s; pausa com aba oculta.
 */
export default function useSupportSync({
  chatId,
  role = 'user',
  enabled = true,
  onMessages,
  onChat,
}) {
  const [error, setError] = useState('');
  const sinceSeqRef = useRef(0);
  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef(null);
  const aliveRef = useRef(true);
  const busyRef = useRef(false);

  const markActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const setSinceSeq = useCallback((seq) => {
    sinceSeqRef.current = Math.max(sinceSeqRef.current, Number(seq) || 0);
  }, []);

  const reset = useCallback((seq = 0) => {
    sinceSeqRef.current = Number(seq) || 0;
    lastActivityRef.current = Date.now();
  }, []);

  const tick = useCallback(async () => {
    if (!aliveRef.current || !enabled || !chatId || busyRef.current) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return;
    }

    busyRef.current = true;
    try {
      const data = await syncSupportChat(chatId, {
        sinceSeq: sinceSeqRef.current,
        role,
      });
      setError('');
      if (data.chat) onChat?.(data.chat);
      if (Array.isArray(data.messages) && data.messages.length) {
        const maxSeq = data.messages.reduce(
          (m, msg) => Math.max(m, msg.seq || 0),
          sinceSeqRef.current
        );
        sinceSeqRef.current = maxSeq;
        lastActivityRef.current = Date.now();
        onMessages?.(data.messages);
      } else if (data.chat?.lastSeq != null) {
        // mantém cursor se servidor avançou sem payload (raro)
        sinceSeqRef.current = Math.max(
          sinceSeqRef.current,
          Number(data.chat.lastSeq) || 0
        );
      }
    } catch (err) {
      setError(err.message || 'Falha ao sincronizar');
    } finally {
      busyRef.current = false;
    }
  }, [chatId, enabled, role, onChat, onMessages]);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!enabled || !chatId) return undefined;

    let cancelled = false;

    const schedule = () => {
      if (cancelled) return;
      const idleMs = Date.now() - lastActivityRef.current;
      const delay = idleMs < 8000 ? 1500 : 5000;
      timerRef.current = setTimeout(async () => {
        await tick();
        schedule();
      }, delay);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        tick();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    tick().finally(() => {
      if (!cancelled) schedule();
    });

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [chatId, enabled, tick]);

  return { error, markActivity, setSinceSeq, reset, syncNow: tick };
}
